import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { DeliveryActivityChartComponent } from '../../delivery-activity-chart/delivery-activity-chart.component';
import { DeliveryActivityService } from 'src/app/services/delivery-activity.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { ShiftConfigService } from 'src/app/services/shift-config.service';
import { DEFAULT_SHIFT_CONFIG, WeekBounds } from 'src/app/services/drill-activity.service';
import { ShiftConfig, shiftConfigFromDto } from 'src/app/shared/models/drilling.models';
import { DeliveryActivityItem, DeliveryBlastRow, SelectedShiftColumn } from 'src/app/shared/models/delivery-activity.models';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { RapportCreationModaleComponent } from '../../rapport-creation-modale/rapport-creation-modale.component';

@Component({
  selector: 'app-delivery-activity-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DeliveryActivityChartComponent],
  templateUrl: './delivery-activity-page.component.html',
  styleUrls: ['./delivery-activity-page.component.scss'],
})
export class DeliveryActivityPageComponent implements OnInit {
  private readonly deliveryActivityService = inject(DeliveryActivityService);
  private readonly shiftConfigService = inject(ShiftConfigService);
  private readonly utilService = inject(UtilisateurService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly siteNav = inject(SiteNavigationService);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(BsModalService);

  protected readonly shiftConfig = signal<ShiftConfig>(DEFAULT_SHIFT_CONFIG);
  protected readonly timeScale = signal<'week' | 'month'>('week');

  private currentBounds: WeekBounds = this.deliveryActivityService.getCurrentWeekBounds();
  protected readonly startDate = signal<Date>(this.currentBounds.start);
  protected readonly endDate = signal<Date>(this.currentBounds.end);

  protected readonly rows = signal<DeliveryBlastRow[]>([]);
  protected readonly loading = signal(false);
  protected readonly selectedShifts = signal<SelectedShiftColumn[]>([]);

  protected readonly selectedDates = computed<string[]>(() => {
    const ordered = new Set(this.selectedShifts().map(shift => shift.date));
    return [...ordered];
  });

  private readonly modalRef = signal<BsModalRef | null>(null);

  protected readonly selectedDeliveryIds = computed<string[]>(() => {
    const selectedShiftKeys = new Set(
      this.selectedShifts().map(shift => `${shift.date}_${shift.shiftIndex}`),
    );

    if (selectedShiftKeys.size === 0) {
      return [];
    }

    const uniqueIds = new Set<string>();
    for (const row of this.rows()) {
      for (const delivery of row.deliveries) {
        const key = `${delivery.date}_${delivery.shiftIndex}`;
        if (selectedShiftKeys.has(key)) {
          uniqueIds.add(delivery.id);
        }
      }
    }
    return [...uniqueIds];
  });

  protected readonly isReportActionDisabled = computed<boolean>(() =>
    this.selectedShifts().length === 0 || this.selectedDeliveryIds().length === 0,
  );

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      // Hosted by the site Planning tab: ignore the navigation that leaves it (e.g. opening a blast),
      // or the period redirect below would merge `mode`/`date` into the destination URL.
      if (params.get('tab') !== 'planning') {
        return;
      }

      const modeParam = params.get('mode');
      const dateParam = params.get('date');

      if (!this.isValidMode(modeParam) || !dateParam) {
        this.navigateToPeriod(this.timeScale(), this.currentBounds.start, true);
        return;
      }

      const parsedDate = this.parseDate(dateParam);
      if (!parsedDate) {
        this.navigateToPeriod(this.timeScale(), this.currentBounds.start, true);
        return;
      }

      const mode = modeParam;
      const bounds = this.computeBounds(mode, parsedDate);
      const canonicalDate = this.toIsoDateString(bounds.start);

      this.timeScale.set(mode);
      this.currentBounds = bounds;
      this.startDate.set(bounds.start);
      this.endDate.set(bounds.end);

      if (canonicalDate !== dateParam) {
        this.navigateToPeriod(mode, bounds.start, true);
        return;
      }

      this.loadData(bounds);
    });
  }

  protected onDateRangeChange(range: { start: Date; end: Date }): void {
    this.navigateToPeriod(this.resolveModeFromRange(range), range.start);
  }

  protected onSelectedShiftsChange(shifts: SelectedShiftColumn[]): void {
    this.selectedShifts.set(shifts);
  }

  protected openReportModal(): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) {
      return;
    }

    const deliveryIds = this.selectedDeliveryIds();
    if (deliveryIds.length === 0) {
      return;
    }

    const initialState = {
      activityReportContext: signal({
        siteId,
        startDate: this.startDate().toISOString(),
        endDate: this.endDate().toISOString(),
        selectedDates: this.selectedDates(),
        selectedShifts: this.selectedShifts(),
        deliveryIds,
        defaultReportName: this.buildDefaultReportName(),
      }),
    };

    this.modalRef.set(this.modalService.show(RapportCreationModaleComponent, {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false,
      initialState,
    }));
  }

  /**
   * La configuration des postes doit être connue *avant* de répartir les livraisons :
   * le poste est figé dans chaque ligne au chargement et n'est pas recalculé ensuite.
   * La charger en parallèle laissait le rendu dépendre de la course entre les deux
   * requêtes — d'où des livraisons rangées dans des colonnes inexistantes après un F5.
   */
  private loadData(bounds: WeekBounds): void {
    const site = this.utilService.currentSite();
    const siteId = site?.siteId;
    if (!siteId) {
      return;
    }

    this.loading.set(true);
    this.shiftConfigService
      .getShiftConfig(siteId)
      .pipe(
        map(dto => shiftConfigFromDto(dto)),
        tap(config => this.shiftConfig.set(config)),
        // A transient failure (expired cache + network hiccup) must not overwrite a
        // previously loaded, valid config with the app-wide default: that would rebuild
        // the chart's columns around it and make already-displayed deliveries appear to
        // move. Fall back to the last known config instead, only ever via this operator.
        catchError(() => of(this.shiftConfig())),
        switchMap(config =>
          this.deliveryActivityService.getDeliveryRowsForWeek(siteId, bounds, config, site?.timezone),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: rows => {
          this.rows.set(rows);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  protected navigateToBlast(row: DeliveryBlastRow): void {
    this.siteNav.navigateFromNodeClick({ id: row.blastId, type: 'blast' }, 'holes', { view: 'delivery' });
  }

  /** Opens the blast on Holes › Delivery with the clicked delivery selected (see HolesDeliveryViewComponent). */
  protected navigateToDelivery(delivery: DeliveryActivityItem): void {
    this.siteNav.navigateFromNodeClick(
      { id: delivery.blastId, type: 'blast' },
      'holes',
      { view: 'delivery', deliveryId: delivery.id },
    );
  }

  private resolveModeFromRange(range: WeekBounds): 'week' | 'month' {
    const days = Math.floor((range.end.getTime() - range.start.getTime()) / 86_400_000) + 1;
    return days > 7 ? 'month' : 'week';
  }

  private computeBounds(mode: 'week' | 'month', date: Date): WeekBounds {
    if (mode === 'month') {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }

    const dow = date.getDay();
    const daysToMonday = dow === 0 ? -6 : 1 - dow;
    const start = new Date(date);
    start.setDate(date.getDate() + daysToMonday);
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  private navigateToPeriod(mode: 'week' | 'month', startDate: Date, replaceUrl = false): void {
    const siteId = this.utilService.currentSite()?.siteId;
    if (!siteId) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { mode, date: this.toIsoDateString(startDate) },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private parseDate(value: string): Date | null {
    const match = /^\d{4}-\d{2}-\d{2}$/.test(value);
    if (!match) {
      return null;
    }

    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toIsoDateString(value: Date): string {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private buildDefaultReportName(): string {
    const start = this.toIsoDateString(this.startDate());
    const end = this.toIsoDateString(this.endDate());
    return `Delivery_reports_${start}_${end}`;
  }

  private isValidMode(value: string | null): value is 'week' | 'month' {
    return value === 'week' || value === 'month';
  }
}
